import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Eye, EyeOff, CheckCircle, XCircle } from "lucide-react";
import { api } from "../App";

const LOGO_RECTANGLE = "https://customer-assets.emergentagent.com/job_hire-race/artifacts/tx9umukp_WhatsApp%20Image%202026-02-22%20at%2015.10.15.jpeg";

export default function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");
  const type = searchParams.get("type") || "reset";

  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [done, setDone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const isInvite = type === "invite";

  useEffect(() => {
    if (!token) {
      setValidating(false);
      setTokenValid(false);
      return;
    }
    const endpoint = isInvite
      ? `/auth/validate-invite?token=${token}`
      : `/auth/validate-reset?token=${token}`;

    api.get(endpoint)
      .then((res) => {
        setTokenValid(true);
        setTokenInfo(res.data);
      })
      .catch(() => {
        setTokenValid(false);
      })
      .finally(() => setValidating(false));
  }, [token, isInvite]);

  const passwordsMatch = password && confirm && password === confirm;
  const passwordStrong = password.length >= 8;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passwordsMatch) {
      toast.error("Passwords don't match.");
      return;
    }
    if (!passwordStrong) {
      toast.error("Password must be at least 8 characters.");
      return;
    }

    setLoading(true);
    try {
      const endpoint = isInvite ? "/auth/accept-invite" : "/auth/reset-password";
      await api.post(endpoint, { token, password });
      setDone(true);
      toast.success(isInvite ? "Account created! You can now sign in." : "Password updated! Sign in with your new password.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong. Try again or request a new link.");
    } finally {
      setLoading(false);
    }
  };

  const title = isInvite ? "SET YOUR PASSWORD" : "RESET PASSWORD";
  const subtitle = isInvite
    ? `Welcome! Set a password for ${tokenInfo?.email || "your account"}.`
    : `Enter a new password for ${tokenInfo?.email || "your account"}.`;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <img src={LOGO_RECTANGLE} alt="Dentsu AMP" className="h-12 mx-auto mb-4" />
          <h1 className="font-heading text-3xl text-text-primary">DREAM MACHINE</h1>
        </div>

        <div className="bg-surface border border-border rounded-lg p-8">
          {validating ? (
            <div className="text-center py-8">
              <div className="animate-pulse text-primary font-heading text-lg">VERIFYING LINK...</div>
            </div>
          ) : !tokenValid ? (
            <div className="text-center py-4">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="font-heading text-xl text-text-primary mb-2">LINK EXPIRED</h2>
              <p className="text-text-muted text-sm mb-6">
                This link is invalid or has expired. Request a new one.
              </p>
              <Button
                onClick={() => navigate("/login")}
                className="bg-primary hover:bg-primary-hover text-white font-heading tracking-wide"
              >
                BACK TO SIGN IN
              </Button>
            </div>
          ) : done ? (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h2 className="font-heading text-xl text-text-primary mb-2">
                {isInvite ? "ACCOUNT READY!" : "PASSWORD UPDATED!"}
              </h2>
              <p className="text-text-muted text-sm mb-6">
                {isInvite
                  ? "Your account is set up. Sign in to get started."
                  : "Your password has been reset. Sign in with your new password."}
              </p>
              <Button
                onClick={() => navigate("/login")}
                className="bg-primary hover:bg-primary-hover text-white font-heading tracking-wide py-5 w-full"
              >
                SIGN IN NOW
              </Button>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h2 className="font-heading text-2xl text-text-primary tracking-wide">{title}</h2>
                <p className="text-text-muted text-sm mt-1">{subtitle}</p>
                {tokenInfo?.name && isInvite && (
                  <p className="text-text-secondary text-sm mt-1">Hi, {tokenInfo.name}! 👋</p>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-text-secondary">
                    New Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="At least 8 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-background border-border text-text-primary placeholder:text-text-muted focus:border-primary pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {password && (
                    <p className={`text-xs mt-1 ${passwordStrong ? "text-green-500" : "text-red-400"}`}>
                      {passwordStrong ? "✓ Strong enough" : "✗ At least 8 characters required"}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm" className="text-text-secondary">
                    Confirm Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirm"
                      type={showConfirm ? "text" : "password"}
                      placeholder="Repeat your password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="bg-background border-border text-text-primary placeholder:text-text-muted focus:border-primary pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
                    >
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {confirm && (
                    <p className={`text-xs mt-1 ${passwordsMatch ? "text-green-500" : "text-red-400"}`}>
                      {passwordsMatch ? "✓ Passwords match" : "✗ Passwords don't match"}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading || !passwordsMatch || !passwordStrong}
                  className="w-full bg-primary hover:bg-primary-hover text-white font-heading text-lg tracking-wide py-6 neon-glow transition-all active:scale-95 disabled:opacity-50"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin">⚡</span>
                      SAVING...
                    </span>
                  ) : isInvite ? (
                    "CREATE ACCOUNT"
                  ) : (
                    "UPDATE PASSWORD"
                  )}
                </Button>
              </form>
            </>
          )}
        </div>

        <p className="mt-8 text-center text-text-muted text-xs">
          Dentsu AMP · Dream Machine Platform
        </p>
      </motion.div>
    </div>
  );
}
